import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar } from "lucide-react";
import { cn } from "../../lib/utils";

interface DateTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  showTimeSelect?: boolean;
  dateFormat?: string;
  timeIntervals?: number;
  disabled?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = "Select date and time",
  id,
  className,
  minDate,
  maxDate,
  showTimeSelect = true,
  dateFormat = "MMM dd, yyyy h:mm aa",
  timeIntervals = 15,
  disabled = false,
}) => {
  const selectedDate = value ? new Date(value) : null;

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Convert to ISO string format (YYYY-MM-DDTHH:mm)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      onChange(formattedDate);
    } else {
      onChange("");
    }
  };

  return (
    <div className="relative">
      <DatePicker
        id={id}
        selected={selectedDate}
        onChange={handleDateChange}
        showTimeSelect={showTimeSelect}
        timeIntervals={timeIntervals}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        className={cn(
          "w-full px-3 py-2 pl-10 pr-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        wrapperClassName="w-full"
        popperPlacement="bottom-start"
        calendarClassName="!bg-white dark:!bg-gray-800 dark:!text-white dark:!border-gray-700"
        dayClassName={(date) =>
          cn(
            "hover:bg-blue-50 dark:hover:bg-blue-900/20",
            date && date.toDateString() === new Date().toDateString() && "!bg-blue-100 dark:!bg-blue-900/30"
          )
        }
      />
      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
    </div>
  );
};

export default DateTimePicker;

